import {
  CUSTOMISATION_CATEGORIES,
  SETTINGS_SCOPE,
} from "@tfg/types/constants/communication-studio.constant"
import { ComponentType } from "@tfg/types/schemas/communication-studio/components"
import {
  ActivitiesComponentCustomisationConfiguration,
  EcoModulationComponentCustomisationConfiguration,
  FooterComponentCustomisationConfiguration,
} from "@tfg/types/schemas/communication-studio/configurationParameters"
import {
  ActivitiesCategoryStylingParameters,
  EcoModulationCategoryStylingParameters,
  FooterCategoryStylingParameters,
  GlobalCategoryStylingParameters,
  LogoCategoryStylingParameters,
  PackagingInfoCategoryStylingParameters,
  ProductAttributesCategoryStylingParameters,
  SearchEngineCategoryStylingParameters,
  GenericPassportTitleCategoryStylingParameters,
} from "@tfg/types/types/communication-studio/styling-parameters.types"

export enum SubscriptionPlan {
  NONE = "NONE",
  STARTER = "STARTER",
  ADVANCED = "ADVANCED",
  EXPERT = "EXPERT",
}

export enum LanguagePermissionLevelEnum {
  READONLY = "readonly_lang",
  EDIT_2 = "edit_2_lang",
  EDIT_3 = "edit_3_lang",
  EDIT_ALL = "edit_all_lang",
}

type LanguagePermissionLevel =
  | LanguagePermissionLevelEnum.READONLY
  | LanguagePermissionLevelEnum.EDIT_2
  | LanguagePermissionLevelEnum.EDIT_3
  | LanguagePermissionLevelEnum.EDIT_ALL

export enum PermissionLevelEnum {
  READONLY = "readonly",
  EDITABLE = "editable",
}

type Booleanify<T> = {
  [K in keyof T]: T[K] extends object ? Booleanify<T[K]> : boolean
}

type GlobalStylingParametersPermissions = Booleanify<
  Partial<GlobalCategoryStylingParameters>
>
type ActivitiesStylingParametersPermissions = Booleanify<
  Partial<ActivitiesCategoryStylingParameters>
>
type PackagingInfoStylingParametersPermissions = Booleanify<
  Partial<PackagingInfoCategoryStylingParameters>
>
type SearchEngineStylingParametersPermissions = Booleanify<
  Partial<SearchEngineCategoryStylingParameters>
>
type FooterStylingParametersPermissions = Booleanify<
  Partial<FooterCategoryStylingParameters>
>
type LogoStylingParametersPermissions = Booleanify<
  Partial<LogoCategoryStylingParameters>
>
type GenericPassportTitleStylingParametersPermissions = Booleanify<
  Partial<GenericPassportTitleCategoryStylingParameters>
>
type ProductAttributesStylingParametersPermissions = Booleanify<
  Partial<ProductAttributesCategoryStylingParameters>
>
type EcoModulationStylingParametersPermissions = Booleanify<
  Partial<EcoModulationCategoryStylingParameters>
>
type StylingParametersPermissions = {
  [CUSTOMISATION_CATEGORIES.GLOBAL]: Partial<GlobalStylingParametersPermissions>
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]: ActivitiesStylingParametersPermissions
  [CUSTOMISATION_CATEGORIES.PACKAGING_INFO]: PackagingInfoStylingParametersPermissions
  [CUSTOMISATION_CATEGORIES.SEARCH_ENGINE]: SearchEngineStylingParametersPermissions
  [CUSTOMISATION_CATEGORIES.FOOTER]: FooterStylingParametersPermissions
  [CUSTOMISATION_CATEGORIES.LOGO]: LogoStylingParametersPermissions
  [CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE]: GenericPassportTitleStylingParametersPermissions
  [CUSTOMISATION_CATEGORIES.PRODUCT_ATTRIBUTES]: ProductAttributesStylingParametersPermissions
  [CUSTOMISATION_CATEGORIES.ECO_MODULATION]: EcoModulationStylingParametersPermissions
}

type FooterComponentCustomisationConfigurationPermissions = Booleanify<
  Partial<FooterComponentCustomisationConfiguration>
>

type ActivitiesComponentCustomisationConfigurationPermissions = Booleanify<
  Partial<ActivitiesComponentCustomisationConfiguration>
>

type EcoModulationComponentCustomisationConfigurationPermissions = Booleanify<
  Partial<EcoModulationComponentCustomisationConfiguration>
>

type ConfigurationsPermissions = {
  [CUSTOMISATION_CATEGORIES.FOOTER]: FooterComponentCustomisationConfigurationPermissions
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]: ActivitiesComponentCustomisationConfigurationPermissions
  [CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE]: GenericPassportTitleStylingParametersPermissions
  [CUSTOMISATION_CATEGORIES.ECO_MODULATION]: EcoModulationComponentCustomisationConfigurationPermissions
}

type PermissionLevel =
  | PermissionLevelEnum.READONLY
  | PermissionLevelEnum.EDITABLE

export type LanguagePermission = {
  level: LanguagePermissionLevel
  description?: string
}

export type GenericPermission = {
  level: PermissionLevel
  description?: string
}

export type AuthorizedDesignTokens = {
  stylingParameters: Partial<StylingParametersPermissions>
  configurations: Partial<ConfigurationsPermissions>
}

export type DesignCustomisationPermission = {
  authorizedDesignTokens?: AuthorizedDesignTokens
} & GenericPermission

type ProductPageComponentsPermission = GenericPermission & {
  components: ComponentType[]
}

export type PermissionsPerScope = {
  [SETTINGS_SCOPE.LANGUAGES]: LanguagePermission
  [SETTINGS_SCOPE.DESIGN_CUSTOMISATION]: DesignCustomisationPermission
  [SETTINGS_SCOPE.COMPONENT_CUSTOMISATION]: GenericPermission
  [SETTINGS_SCOPE.CONTENT_FILTERING]: GenericPermission
  [SETTINGS_SCOPE.PRODUCT_PAGE_COMPONENTS]: ProductPageComponentsPermission
}
